import * as React from 'react';
import { useState } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors  disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        outlineDestructive:
          'border border-destructive text-destructive bg-background',
        ghostDestructive: 'text-destructive',
        ghostSuccess: 'text-success',
        outlineFg: 'border border-border text-foreground bg-background',
        outline: 'border border-primary text-primary bg-background',
        secondary: 'bg-secondary text-secondary-foreground',
        ghost: '',
        ghostSecondary: 'bg-background-secondary text-foreground',
        ghostWarning: 'text-warning',
        ghostInfo: 'text-info',
        link: 'text-primary underline-offset-4',
        enhanced: 'bg-banner-accent text-banner-text',
      },
      hoverVariant: {
        none: '',
        default: 'bg-primary/90',
        destructive: 'bg-destructive/90',
        outline: 'bg-primary text-primary-foreground',
        outlineFg: 'bg-card border-primary text-primary',
        outlineDestructive: 'bg-destructive text-destructive-foreground',
        secondary: 'bg-secondary/80',
        ghost: 'bg-muted text-foreground',
        ghostSecondary: 'bg-muted',
        link: 'underline',
        ghostDestructive: 'bg-destructive/10',
        ghostSuccess: 'bg-success/10',
        ghostWarning: 'bg-warning/10',
        ghostInfo: 'bg-info/10',
        enhanced: 'bg-muted',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLElement>,
    VariantProps<typeof buttonVariants> {
  square?: boolean;
  asChild?: boolean;
}

export function isMobile() {
  const regex =
    /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, square, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const [hoverVariant, setHoverVariant] = useState<
      VariantProps<typeof buttonVariants>['hoverVariant'] | 'none'
    >('none');

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, hoverVariant, size, className }),
          square ? 'rounded-lg' : ''
        )}
        ref={ref}
        onMouseEnter={(...args) => {
          if (!isMobile()) {
            setHoverVariant(variant || 'default');
          }
          props.onMouseEnter?.(...args);
        }}
        onMouseLeave={(...args) => {
          if (!isMobile()) {
            setHoverVariant('none');
          }
          props.onMouseLeave?.(...args);
        }}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
